import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import ComplexFilterWidget from "../Filter/ComplexFilterWidget";
import {
  buildAppliedFilterChipData,
  hydrateFilterModuleFilters,
} from "../Filter/appliedFilterChipsUtils";
import QuickFilterIcon from "./QuickFilterIcon";
import {
  createEmptyFilterModuleFilters,
  createQuickFilterId,
  getPageQuickFilters,
  hasQuickFilterRules,
  MAX_USER_QUICK_FILTERS_PER_PAGE,
} from "./quickFilterUtils";
import { getSystemQuickFilters } from "./quickFilterPresets";
import {
  deleteUserQuickFilterOnPage,
  renameUserQuickFilterOnPage,
  upsertUserQuickFilterOnPage,
} from "./quickFilterApi";

const ACCENT = "#6759FF";
const ACCENT_SOFT = "rgba(103, 89, 255, 0.12)";

const VIEW = {
  LIST: "list",
  CREATE: "create",
  EDIT: "edit",
};

const NamePromptDialog = ({
  open,
  title,
  label,
  initialValue = "",
  confirmLabel = "Save",
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [name, setName] = useState(initialValue);

  useEffect(() => {
    if (open) setName(initialValue || "");
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={label}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && name.trim()) {
              onConfirm?.(name.trim());
            }
          }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loading || !name.trim()}
          onClick={() => onConfirm?.(name.trim())}
          sx={{ textTransform: "none", bgcolor: ACCENT }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmDeleteDialog = ({
  open,
  filterName = "",
  onClose,
  onConfirm,
  loading = false,
}) => (
  <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete Quick Filter</DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: 14, color: "#4A4458", mt: 0.5 }}>
        Delete quick filter <strong>"{filterName}"</strong>? This cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color="error"
        disabled={loading}
        onClick={onConfirm}
        sx={{ textTransform: "none" }}
      >
        {loading ? <CircularProgress size={18} color="inherit" /> : "Delete"}
      </Button>
    </DialogActions>
  </Dialog>
);

const QuickFilterRulePreview = ({ filter, filterConfig }) => {
  const chips = useMemo(
    () => buildAppliedFilterChipData(filter?.filterModuleFilters, filterConfig),
    [filter?.filterModuleFilters, filterConfig]
  );

  if (!chips.length) {
    const fallback = filter?.description || "";
    if (!fallback) return null;
    return (
      <Typography
        sx={{
          fontSize: 10,
          color: "#8A8699",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          mt: 0.25,
        }}
        title={fallback}
      >
        {fallback}
      </Typography>
    );
  }

  const fullTitle = chips
    .map((chip, index) => {
      const rule = [chip.fieldLabel, chip.operatorLabel, chip.valueLabel]
        .filter((part) => `${part}`.trim() !== "")
        .join(" ");
      const formattedRule = chip.sectionLabel
        ? `${chip.sectionLabel} ${rule}`.trim()
        : rule;
      if (index === 0) return formattedRule;
      return `${chip.joiner || "and"} ${formattedRule}`;
    })
    .join(" ");

  return (
    <Box
      title={fullTitle}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        maxHeight: 44,
        overflow: "hidden",
      }}
    >
      {chips.map((chip, index) => (
        <Box
          key={chip.key}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {index > 0 ? (
            <Typography
              component="span"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "#8A8699",
                textTransform: "uppercase",
                mx: 0.25,
              }}
            >
              {chip.joiner || "and"}
            </Typography>
          ) : null}
          {chip.sectionLabel ? (
            <Typography
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: "#5C576B",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {chip.sectionLabel}
            </Typography>
          ) : null}
          {chip.fieldLabel ? (
            <Typography
              component="span"
              sx={{ fontSize: 10, color: "#5C576B", fontWeight: 500 }}
            >
              {chip.fieldLabel}
            </Typography>
          ) : null}
          {chip.operatorLabel ? (
            <Chip
              size="small"
              label={chip.operatorLabel}
              sx={{
                height: 18,
                fontSize: 9,
                fontWeight: 600,
                bgcolor: "grey.100",
                color: "grey.700",
                borderRadius: "4px",
                "& .MuiChip-label": {
                  px: 0.75,
                  py: 0,
                },
              }}
            />
          ) : null}
          {chip.valueLabel !== "" && chip.valueLabel != null ? (
            <Typography
              component="span"
              sx={{
                fontSize: 10,
                color: "#5C576B",
                fontWeight: 500,
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {chip.valueLabel}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const SystemPresetCard = ({ filter, filterConfig, onApply, applyingId }) => {
  const isApplying = applyingId === filter.id;

  return (
    <Box
      onClick={() => {
        if (!isApplying) onApply?.(filter);
      }}
      sx={{
        width: { xs: "100%", sm: "calc(50% - 4px)" },
        maxWidth: "100%",
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: isApplying ? ACCENT_SOFT : "white",
        cursor: isApplying ? "wait" : "pointer",
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: ACCENT,
          bgcolor: ACCENT_SOFT,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#2F2B3A",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {filter.name}
          </Typography>
          <QuickFilterRulePreview filter={filter} filterConfig={filterConfig} />
        </Box>
        {isApplying ? <CircularProgress size={14} sx={{ color: ACCENT }} /> : null}
      </Stack>
    </Box>
  );
};

const UserQuickFilterCard = ({
  filter,
  filterConfig,
  onApply,
  onEdit,
  onRename,
  onDelete,
  applyingId,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isApplying = applyingId === filter.id;

  return (
    <Box
      onClick={() => {
        if (!isApplying) onApply?.(filter);
      }}
      sx={{
        width: { xs: "100%", sm: "calc(50% - 4px)" },
        maxWidth: "100%",
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "grey.200",
        bgcolor: isApplying ? ACCENT_SOFT : "white",
        cursor: isApplying ? "wait" : "pointer",
        transition: "all 0.15s ease",
        position: "relative",
        "&:hover": {
          borderColor: ACCENT,
          bgcolor: ACCENT_SOFT,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        <Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: "#2F2B3A",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {filter.name}
          </Typography>
          <QuickFilterRulePreview filter={filter} filterConfig={filterConfig} />
        </Box>
        {isApplying ? (
          <CircularProgress size={14} sx={{ color: ACCENT, mt: 0.25 }} />
        ) : (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setMenuAnchorEl(event.currentTarget);
            }}
            sx={{ color: "#8A8699", p: 0.25, mt: -0.25, flexShrink: 0 }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Stack>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ dense: true }}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            onEdit?.(filter);
          }}
          sx={{ fontSize: 13 }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            onRename?.(filter);
          }}
          sx={{ fontSize: 13 }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            onDelete?.(filter);
          }}
          sx={{ fontSize: 13, color: "#d32f2f" }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

const FilterSection = ({ title, filters, variant = "user", ...cardProps }) => {
  if (!filters?.length) return null;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: "#8A8699",
          px: 0.5,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {filters.map((filter) =>
          variant === "system" ? (
            <SystemPresetCard
              key={filter.id}
              filter={filter}
              {...cardProps}
            />
          ) : (
            <UserQuickFilterCard
              key={filter.id}
              filter={filter}
              {...cardProps}
            />
          )
        )}
      </Box>
    </Box>
  );
};

const QuickFilterModal = ({
  open,
  onClose,
  clientId,
  platform,
  pageId,
  filterConfig,
  campaignFilter = false,
  narrowFilters = false,
  onApply,
}) => {
  const { quickFilterConfig, setQuickFilterConfig } = useGlobalContext();
  const [view, setView] = useState(VIEW.LIST);
  const [saving, setSaving] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [editorName, setEditorName] = useState("");
  const [editorFilters, setEditorFilters] = useState(createEmptyFilterModuleFilters());
  const [editingFilter, setEditingFilter] = useState(null);
  const [draftReady, setDraftReady] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const systemFilters = useMemo(
    () => getSystemQuickFilters(pageId, platform),
    [pageId, platform]
  );

  const userFilters = useMemo(
    () => getPageQuickFilters(quickFilterConfig?.value, pageId).filters,
    [quickFilterConfig, pageId]
  );

  const isUserQuickFilterLimitReached =
    userFilters.length >= MAX_USER_QUICK_FILTERS_PER_PAGE;

  const resetEditor = () => {
    setEditorName("");
    setEditorFilters(createEmptyFilterModuleFilters());
    setEditingFilter(null);
    setDraftReady(false);
    setErrorMessage("");
  };

  useEffect(() => {
    if (!open) return;
    setView(VIEW.LIST);
    resetEditor();
  }, [open, pageId]);

  const handleClose = () => {
    if (saving || applyingId) return;
    onClose?.();
  };

  const handleApplyFilter = async (filter) => {
    if (!filter?.filterModuleFilters) return;
    setApplyingId(filter.id);
    try {
      // Re-resolve system presets so relative dates (e.g. today-7) stay fresh.
      const freshSystemFilter = getSystemQuickFilters(pageId, platform).find(
        (item) => item.id === filter.id
      );
      const filterModuleFilters =
        freshSystemFilter?.filterModuleFilters ?? filter.filterModuleFilters;
      const hydratedFilters = hydrateFilterModuleFilters(
        filterModuleFilters,
        filterConfig
      );
      await onApply?.(hydratedFilters);
      onClose?.();
    } finally {
      setApplyingId(null);
    }
  };

  const openCreate = () => {
    if (isUserQuickFilterLimitReached) {
      toast.info(
        `You can create up to ${MAX_USER_QUICK_FILTERS_PER_PAGE} quick filters per page.`
      );
      return;
    }
    resetEditor();
    setView(VIEW.CREATE);
  };

  const openEdit = (filter) => {
    setEditingFilter(filter);
    setEditorName(filter.name || "");
    setEditorFilters(filter.filterModuleFilters || createEmptyFilterModuleFilters());
    setDraftReady(hasQuickFilterRules(filter.filterModuleFilters));
    setErrorMessage("");
    setView(VIEW.EDIT);
  };

  const backToList = () => {
    resetEditor();
    setView(VIEW.LIST);
  };

  const handleEditorFilterOutput = (filters) => {
    setEditorFilters(filters);
    setDraftReady(hasQuickFilterRules(filters));
    setErrorMessage("");
  };

  const handleSaveEditor = async () => {
    const trimmedName = editorName.trim();
    if (!trimmedName) {
      setErrorMessage("Please enter a quick filter name");
      return;
    }
    if (!draftReady || !hasQuickFilterRules(editorFilters)) {
      setErrorMessage("Configure filters and click Apply before saving");
      return;
    }
    if (
      view === VIEW.CREATE &&
      userFilters.length >= MAX_USER_QUICK_FILTERS_PER_PAGE
    ) {
      setErrorMessage(
        `You can create up to ${MAX_USER_QUICK_FILTERS_PER_PAGE} quick filters per page.`
      );
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const payload = {
        id: editingFilter?.id || createQuickFilterId(),
        name: trimmedName,
        description: "",
        filterModuleFilters: editorFilters,
        createdAt: editingFilter?.createdAt || new Date().toISOString(),
      };
      const nextConfig = await upsertUserQuickFilterOnPage({
        clientId,
        platform,
        pageId,
        filter: payload,
        existingConfig: quickFilterConfig,
      });
      setQuickFilterConfig(nextConfig);
      toast.success(
        view === VIEW.EDIT
          ? "Quick filter updated successfully"
          : "Quick filter saved successfully"
      );
      backToList();
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.message ??
          "Unable to save quick filter"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (name) => {
    if (!renameTarget) return;
    setSaving(true);
    try {
      const nextConfig = await renameUserQuickFilterOnPage({
        clientId,
        platform,
        pageId,
        filterId: renameTarget.id,
        name,
        existingConfig: quickFilterConfig,
      });
      setQuickFilterConfig(nextConfig);
      setRenameTarget(null);
      toast.success("Quick filter renamed successfully");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.message ??
          "Unable to rename quick filter"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = (filter) => {
    setDeleteTarget(filter);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;

    setSaving(true);
    setErrorMessage("");
    try {
      const nextConfig = await deleteUserQuickFilterOnPage({
        clientId,
        platform,
        pageId,
        filterId: deleteTarget.id,
        existingConfig: quickFilterConfig,
      });
      setQuickFilterConfig(nextConfig);
      setDeleteTarget(null);
      toast.success("Quick filter deleted successfully");
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ??
          error?.message ??
          "Unable to delete quick filter"
      );
    } finally {
      setSaving(false);
    }
  };

  const isEditorView = view === VIEW.CREATE || view === VIEW.EDIT;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isEditorView ? false : "sm"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
            ...(isEditorView
              ? {
                  width: "100%",
                  maxWidth: 1050,
                }
              : {}),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pr: 1,
            pb: 1.5,
          }}
        >
          {isEditorView ? (
            <IconButton size="small" onClick={backToList} disabled={saving}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          ) : (
            <QuickFilterIcon fontSize={22} />
          )}
          <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
            {view === VIEW.CREATE
              ? "Create Quick Filter"
              : view === VIEW.EDIT
                ? "Edit Quick Filter"
                : "Quick Filter"}
          </Typography>
          <IconButton size="small" onClick={handleClose} disabled={saving || Boolean(applyingId)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          {errorMessage ? (
            <Typography sx={{ color: "#d32f2f", fontSize: 12, mb: 1.5 }}>
              {errorMessage}
            </Typography>
          ) : null}

              {view === VIEW.LIST && (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: isUserQuickFilterLimitReached ? 0.75 : 2 }}
              >
                <Typography sx={{ fontSize: 12, color: "#8A8699" }}>
                  Apply a preset or create your own quick filter.
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  disabled={isUserQuickFilterLimitReached}
                  sx={{
                    textTransform: "none",
                    color: ACCENT,
                    fontWeight: 600,
                    bgcolor: ACCENT_SOFT,
                    px: 1.5,
                    minWidth: "auto",
                    "& .MuiButton-startIcon": {
                      mr: 0.5,
                      ml: 0,
                    },
                    "&:hover": { bgcolor: "rgba(103, 89, 255, 0.18)" },
                    "&.Mui-disabled": {
                      color: "#9E9AA8",
                      bgcolor: "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  Create
                </Button>
              </Stack>
              {isUserQuickFilterLimitReached ? (
                <Typography sx={{ fontSize: 11, color: "#d32f2f", mb: 2 }}>
                  Maximum {MAX_USER_QUICK_FILTERS_PER_PAGE} quick filters allowed
                  per page. Delete one to create another.
                </Typography>
              ) : null}

              <>
                  <FilterSection
                    title="SYSTEM PRESETS"
                    variant="system"
                    filters={systemFilters}
                    filterConfig={filterConfig}
                    onApply={handleApplyFilter}
                    applyingId={applyingId}
                  />
                  <FilterSection
                    title="MY QUICK FILTERS"
                    variant="user"
                    filters={userFilters}
                    filterConfig={filterConfig}
                    onApply={handleApplyFilter}
                    onEdit={openEdit}
                    onRename={setRenameTarget}
                    onDelete={handleDeleteRequest}
                    applyingId={applyingId}
                  />
                  {!systemFilters.length && !userFilters.length ? (
                    <Typography sx={{ fontSize: 13, color: "#8A8699", textAlign: "center", py: 4 }}>
                      No quick filters yet. Create one to get started.
                    </Typography>
                  ) : null}
                  {systemFilters.length > 0 && !userFilters.length ? (
                    <Typography sx={{ fontSize: 12, color: "#8A8699", px: 0.5 }}>
                      No saved quick filters yet.
                    </Typography>
                  ) : null}
              </>
            </>
          )}

          {isEditorView && (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Quick filter name"
                value={editorName}
                onChange={(event) => setEditorName(event.target.value)}
                fullWidth
              />
              <Typography sx={{ fontSize: 12, color: "#8A8699" }}>
                Configure filters below, click Apply on the filter panel, then save.
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 1.5,
                  overflow: "visible",
                  bgcolor: "#fff",
                }}
              >
                <ComplexFilterWidget
                  key={`${view}-${editingFilter?.id || "new"}`}
                  config={filterConfig}
                  campaignFilter={campaignFilter}
                  filters={editorFilters}
                  showPopupModal={false}
                  setShowPopupModal={() => {}}
                  output={handleEditorFilterOutput}
                  prefixMember=""
                  type="page"
                  sidebar={true}
                  default="metrics"
                  page="overlay"
                  isAppliedCallback={() => {}}
                  summary="always"
                  narrowFilters={narrowFilters}
                  summaryOutput={() => {}}
                  calloutTitle="Select your filters"
                  calloutContent="+ Add Filter"
                  hideFilterSummary={true}
                  hideCancel={true}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>

        {isEditorView ? (
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button onClick={backToList} disabled={saving} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEditor}
              disabled={saving}
              sx={{ textTransform: "none", bgcolor: ACCENT }}
            >
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : view === VIEW.EDIT ? (
                "Update Quick Filter"
              ) : (
                "Save Quick Filter"
              )}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>

      <NamePromptDialog
        open={Boolean(renameTarget)}
        title="Rename Quick Filter"
        label="Quick filter name"
        initialValue={renameTarget?.name || ""}
        confirmLabel="Rename"
        loading={saving}
        onClose={() => (saving ? null : setRenameTarget(null))}
        onConfirm={handleRename}
      />
      <ConfirmDeleteDialog
        open={Boolean(deleteTarget)}
        filterName={deleteTarget?.name || ""}
        loading={saving}
        onClose={() => (saving ? null : setDeleteTarget(null))}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default QuickFilterModal;
